import { useEffect, useState } from "react";
import type { BodyTypeSlug, Customer } from "@/lib/account-api";

const ACCOUNT_AVATAR_GRADIENT =
  "linear-gradient(135deg, var(--light-taupe), var(--dusty-rose))";
const AVATAR_CDN_BASE = "https://cdn.capsulechichub.com/avatars";

function avatarUrlFor(bodyType: BodyTypeSlug | null): string {
  return `${AVATAR_CDN_BASE}/avatar-${bodyType ?? "default"}.jpg`;
}

function deriveInitials(c: Customer | null): string {
  if (!c) return "";
  const first = c.firstName?.trim()?.[0];
  const last = c.lastName?.trim()?.[0];
  if (first && last) return (first + last).toUpperCase();
  if (first) return first.toUpperCase();
  return c.email.trim()[0]?.toUpperCase() ?? "";
}

interface AccountAvatarProps {
  customer: Customer | null;
  size?: number;
  fontSize?: number;
}

export default function AccountAvatar({
  customer,
  size = 34,
  fontSize,
}: AccountAvatarProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const initials = deriveInitials(customer);
  const bodyType = customer?.bodyType ?? null;
  useEffect(() => {
    setImgFailed(false);
  }, [bodyType]);
  return (
    <span
      className="relative flex items-center justify-center text-charcoal font-serif font-semibold overflow-hidden shrink-0"
      style={{
        width: size,
        height: size,
        borderRadius: 9999,
        background: ACCOUNT_AVATAR_GRADIENT,
        fontSize: fontSize ?? Math.round(size * 0.4),
      }}
    >
      {initials && <span aria-hidden="true">{initials}</span>}
      {!imgFailed && (
        <img
          src={avatarUrlFor(bodyType)}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setImgFailed(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
    </span>
  );
}
